import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useI18n } from "../../lib/i18n";

const AUDIENCE_IMG =
    "https://customer-assets.emergentagent.com/job_ftak-productions/artifacts/tmbnzit7_IMG_8046.jpg";

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Speaking() {
    const { t } = useI18n();
    const s = t.speaking;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

    return (
        <section
            id="speaking"
            ref={ref}
            className="section relative bg-[#050505] overflow-hidden"
            data-testid="speaking-section"
        >
            <div className="container-x">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    {/* Copy */}
                    <div className="lg:col-span-5 lg:order-2">
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeUp}
                            className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6"
                        >
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </motion.div>

                        <motion.h2
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeUp}
                            className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.03em] leading-[1.02]"
                            data-testid="speaking-headline"
                        >
                            {s.titlePre}
                            <span className="italic font-extralight text-[#E55B13]">
                                {s.titleAccent}
                            </span>
                            {s.titlePost}
                        </motion.h2>

                        <motion.p
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                            variants={fadeUp}
                            custom={1}
                            className="mt-6 font-body text-white/70 text-lg leading-relaxed max-w-lg"
                        >
                            {s.intro}
                        </motion.p>

                        <motion.p
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                            variants={fadeUp}
                            custom={2}
                            className="mt-4 font-body text-white/55 text-base leading-relaxed max-w-lg"
                        >
                            {s.body}
                        </motion.p>

                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                            variants={fadeUp}
                            custom={3}
                            className="mt-10"
                        >
                            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/40 mb-5">
                                {s.servicesLabel}
                            </div>
                            <ul className="grid grid-cols-1 gap-2 max-w-md">
                                {s.services.map((svc, i) => (
                                    <li
                                        key={svc}
                                        className="flex items-center gap-3 font-body text-[15px] md:text-base text-white/85"
                                        data-testid={`speaking-service-${i}`}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#E55B13]" />
                                        {svc}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>

                    {/* Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 60 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{
                            duration: 1.1,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="lg:col-span-7 lg:order-1 relative rounded-[28px] overflow-hidden border border-white/[0.08] bg-[#0a0a0a] aspect-[4/5] md:aspect-[16/11]"
                    >
                        <motion.img
                            style={{ y }}
                            src={AUDIENCE_IMG}
                            alt="Ftaklakis Media"
                            className="w-full h-[115%] -mt-[7%] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20 pointer-events-none" />
                        <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-[10px] font-mono tracking-[0.22em] uppercase text-white/70">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E55B13]" />
                            {s.badge}
                        </div>
                        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                            <div>
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/60">
                                    {s.caption}
                                </div>
                                <div className="mt-1 font-heading text-lg md:text-xl font-light text-white">
                                    {s.captionSub}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
